import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, UserPlus, Phone, Trash2, Edit3, ShieldAlert, Star, AlertCircle, Heart, Loader2, Info } from 'lucide-react';
import { AddFamilyMemberModal } from './AddFamilyMemberModal';
import { useAuth } from '../../context/AuthContext';
import { toast } from '../../hooks/use-toast';
import { Link, useNavigate } from 'react-router-dom';
import { usePageMeta } from '../../hooks/usePageMeta';

import { BACKEND_URL } from '../../config';
const API_BASE = BACKEND_URL;

export const FamilyPage = () => {
  usePageMeta(
    'Family and Emergency Contacts - Emergency Family Circle',
    'Stay connected with your loved ones while travelling by train. Manage family and emergency contacts easily with TrainPanda’s Emergency Family Circle.'
  );
  const { user } = useAuth();
  const nav = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMember, setEditMember] = useState(null);

  const handleAddMemberClick = () => {
    const token = localStorage.getItem('tp_token');
    if (!user && !token) {
      toast({
        title: 'Login Required',
        description: 'Please login with your mobile number to add and manage family members.',
      });
      nav('/login');
      return;
    }
    setEditMember(null);
    setModalOpen(true);
  };

  const fetchMembers = useCallback(async () => {
    const token = localStorage.getItem('tp_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API_BASE}/api/family/members`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMembers(res.data.members || []);
    } catch (err) {
      toast({ title: 'Failed to load family members', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleDelete = async (memberId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from your emergency family contacts?`)) return;

    const token = localStorage.getItem('tp_token');
    try {
      await axios.delete(`${API_BASE}/api/family/members/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: `${memberName} removed` });
      fetchMembers();
    } catch (err) {
      toast({ title: 'Error deleting member', variant: 'destructive' });
    }
  };

  const getRelationshipBadgeColor = (rel) => {
    const map = {
      Parent: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      Spouse: 'bg-rose-50 text-rose-700 border-rose-200',
      Sibling: 'bg-amber-50 text-amber-700 border-amber-200',
      Child: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      Friend: 'bg-sky-50 text-sky-700 border-sky-200',
    };
    return map[rel] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const slotsRemaining = Math.max(0, 5 - members.length);

  return (
    <div className="py-4 md:py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Banner Header */}
        <div className="rounded-3xl bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 text-white p-6 sm:p-7 shadow-xl shadow-orange-500/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md text-xs font-black uppercase tracking-wider text-yellow-200">
              <Users className="w-3.5 h-3.5" /> SSO-Linked Emergency Network
            </div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight">
              Family & Emergency Contacts
            </h1>
            <p className="text-xs sm:text-sm text-orange-100 font-medium leading-relaxed">
              Add up to 5 family members. When you press <b>SOS</b> on any train, live GPS location and siren push alerts are instantly dispatched to all added members.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 flex flex-col items-center justify-center shrink-0 w-full sm:w-auto">
            <span className="text-3xl font-black text-white">{members.length} / 5</span>
            <span className="text-[11px] font-bold text-orange-100 uppercase tracking-wider mt-0.5">
              Contacts Added
            </span>
          </div>
        </div>

        {/* Not Logged In Warning */}
        {!user && !localStorage.getItem('tp_token') && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
              <p className="text-xs text-amber-900 font-semibold">
                Please login with your mobile number to securely save & sync family members with your account.
              </p>
            </div>
            <Link
              to="/login"
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold shrink-0 transition-colors"
            >
              Login with OTP
            </Link>
          </div>
        )}

        {/* Action Header & Quota */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-black text-gray-900">Your Emergency Family Circle</h2>
            <p className="text-xs text-gray-500">
              {slotsRemaining > 0
                ? `${slotsRemaining} more contact slot${slotsRemaining > 1 ? 's' : ''} available`
                : 'Maximum quota reached (5 of 5 contacts added)'}
            </p>
          </div>

          <button
            onClick={handleAddMemberClick}
            disabled={slotsRemaining === 0}
            className={`px-5 py-2.5 rounded-xl font-bold text-xs flex items-center gap-2 shadow-md transition-all ${
              slotsRemaining > 0
                ? 'bg-orange-600 hover:bg-orange-700 text-white shadow-orange-500/20'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
            }`}
          >
            <UserPlus className="w-4 h-4" />
            Add Family Member
          </button>
        </div>

        {/* Members Cards List */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 className="w-8 h-8 text-orange-600 animate-spin" />
            <p className="text-xs text-gray-500 font-medium">Loading family circle...</p>
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-14 bg-white rounded-3xl border-2 border-dashed border-gray-200 p-8 space-y-4">
            <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
              <Heart className="w-8 h-8" />
            </div>
            <div className="space-y-1">
              <h3 className="text-base font-bold text-gray-900">No Family Contacts Added Yet</h3>
              <p className="text-xs text-gray-500 max-w-md mx-auto">
                Add your parents, spouse, or siblings so they receive your live location and emergency sirens whenever you trigger SOS on train.
              </p>
            </div>
            <button
              onClick={handleAddMemberClick}
              className="px-6 py-3 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-bold text-xs shadow-md shadow-orange-500/20 transition-all inline-flex items-center gap-2"
            >
              <UserPlus className="w-4 h-4" /> Add First Family Member
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {members.map((m) => (
              <div
                key={m.id}
                className="bg-white rounded-2xl p-5 border border-gray-200 hover:border-orange-300 shadow-sm hover:shadow-md transition-all flex flex-col justify-between space-y-4 relative overflow-hidden group"
              >
                {/* Primary Tag */}
                {m.is_primary && (
                  <div className="absolute top-0 right-0 bg-gradient-to-l from-orange-600 to-amber-600 text-white text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-bl-xl flex items-center gap-1 shadow-sm">
                    <Star className="w-3 h-3 fill-yellow-200 text-yellow-200" /> Primary Contact
                  </div>
                )}

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-100 to-amber-100 text-orange-700 font-black text-lg flex items-center justify-center shadow-inner shrink-0">
                      {m.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-base group-hover:text-orange-600 transition-colors">
                        {m.name}
                      </h3>
                      <span
                        className={`inline-flex text-[11px] font-bold px-2.5 py-0.5 rounded-full border mt-1 ${getRelationshipBadgeColor(
                          m.relationship
                        )}`}
                      >
                        {m.relationship}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 bg-gray-50 p-2.5 rounded-xl border border-gray-100">
                    <Phone className="w-3.5 h-3.5 text-orange-600" />
                    <span>+91 {m.contact_number}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <a
                    href={`tel:${m.contact_number}`}
                    className="flex items-center gap-1.5 text-xs font-bold text-emerald-700 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-lg transition-colors"
                  >
                    <Phone className="w-3 h-3" /> Call
                  </a>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => { setEditMember(m); setModalOpen(true); }}
                      className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                      title="Edit Contact"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(m.id, m.name)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete Contact"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

              </div>
            ))}
          </div>
        )}

        {/* Info Box */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-3xl p-6 flex items-start gap-4">
          <div className="w-10 h-10 rounded-2xl bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-md shadow-blue-500/20">
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div className="space-y-1 text-xs text-blue-900 leading-relaxed">
            <h4 className="font-bold text-sm">How Emergency SOS Alerting Works:</h4>
            <p>
              Whenever you press the <b>SOS</b> button, TrainPanda fetches your live GPS coordinates, attaches your train & coach info, and fires an instant emergency push notification + siren audio to all {members.length} added family contacts.
            </p>
          </div>
        </div>

      </div>

      {/* Add / Edit Modal */}
      <AddFamilyMemberModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={fetchMembers}
        editMember={editMember}
      />
    </div>
  );
};
export default FamilyPage;
